import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ClipLoader } from 'react-spinners';
import Pusher from 'pusher-js';
import MenuSelect from './MenuSelect';

const QueueDisplay = () => {
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [inPreparation, setInPreparation] = useState([]);
  const [readyToServe, setReadyToServe] = useState([]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [showPendingSection, setShowPendingSection] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [updatingTicket, setUpdatingTicket] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState('All');
  const queueDisplayRef = useRef(null);
  const fetchTimeoutRef = useRef(null);

  const extractNumbers = (str) => {
    if (typeof str !== 'string') return str;
    const match = str.match(/\(\s*(\d+)\s*\)/);
    return match ? match[1] : null;
  };

  const fetchQueueData = useCallback(async (isBackground = false) => {
    if (isBackground) setBackgroundLoading(true);
    else setLoading(true);
    const id_account_caisse = localStorage.getItem('id_account_caisse');
    const id_caisse = localStorage.getItem('id_caisse');
    if (id_account_caisse && id_caisse) {
      try {
        const queueResponse = await axios.get(
          `https://apiTicket.lalivraison.ma/api/queue/${id_account_caisse}/${id_caisse}/${encodeURIComponent(selectedMenu)}`,
          { timeout: 5000 }
        );
        setInPreparation(queueResponse.data.neworders || []);
        setReadyToServe(queueResponse.data.readyorders || []);

        const pendingResponse = await axios.get(
          `https://apiTicket.lalivraison.ma/api/pending-tickets/${id_account_caisse}/${id_caisse}`,
          { timeout: 5000 }
        );
        setPendingOrders(pendingResponse.data.pendingOrders || []);

        setError('');
        setLastUpdated(new Date());
      } catch (error) {
        console.error('Error fetching queue data:', error);
        if (error.code === 'ECONNABORTED') {
          setError('Request timed out. Retrying...');
        } else {
          setError('Failed to fetch queue data.');
        }
      } finally {
        if (isBackground) setBackgroundLoading(false);
        else setLoading(false);
      }
    } else {
      localStorage.removeItem('isAuthenticated');
      navigate('/');
    }
  }, [selectedMenu, navigate]);

  const checkPlafondBorne = useCallback(async () => {
    const id_caisse = localStorage.getItem('id_caisse');
    if (id_caisse) {
      try {
        const response = await axios.get(
          `https://apiTicket.lalivraison.ma/api/check-plafond-borne/${id_caisse}`,
          { timeout: 5000 }
        );
        setShowPendingSection(response.data.showPending);
      } catch (error) {
        console.error('Error checking plafond_borne:', error);
        setError('Failed to check plafond_borne.');
      }
    }
  }, []);

  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
    if (!isAuthenticated) {
      navigate('/');
      return;
    }

    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    checkPlafondBorne();
    fetchQueueData();

    const refreshInterval = setInterval(() => fetchQueueData(true), 15000);

    const id_account_caisse = localStorage.getItem('id_account_caisse');
    if (id_account_caisse) {
      const pusher = new Pusher('774d683fd442262b8e0b', {
        cluster: 'eu',
        encrypted: true,
      });

      const channel = pusher.subscribe('live_orders_livraison.' + id_account_caisse);

      channel.bind('reload-tickets', (data) => {
        if (data.source === 'reload-tickets') {
          if (fetchTimeoutRef.current) {
            clearTimeout(fetchTimeoutRef.current);
          }
          fetchTimeoutRef.current = setTimeout(() => {
            fetchQueueData(true);
          }, 500);
        }
      });

      return () => {
        channel.unbind_all();
        channel.unsubscribe();
        pusher.disconnect();
        clearInterval(timer);
        clearInterval(refreshInterval);
        if (fetchTimeoutRef.current) {
          clearTimeout(fetchTimeoutRef.current);
        }
      };
    }

    return () => {
      clearInterval(timer);
      clearInterval(refreshInterval);
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [navigate, fetchQueueData, checkPlafondBorne]);

  const formatDateTime = (date) => {
    return date.toLocaleString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('id_account_caisse');
    localStorage.removeItem('id_caisse');
    navigate('/');
  };

  const handleTicketClickToReady = async (ticket) => {
    setUpdatingTicket(ticket.id_ticket_cle);
    const id_account_caisse = localStorage.getItem('id_account_caisse');

    try {
      const response = await axios.post(
        'https://apiTicket.lalivraison.ma/api/update-ticket-pret',
        {
          id_ticket_cle: ticket.id_ticket_cle,
          restaurantId: ticket.id_caisse,
          id_account_caisse,
          nom_imprimante: selectedMenu !== 'All' ? selectedMenu : undefined,
        },
        { timeout: 5000 }
      );

      if (response.status === 200) {
        setError('');
        await fetchQueueData();
      }
    } catch (error) {
      console.error('Error updating ticket to ready:', error);
      setError(error.response?.data?.message || 'Failed to mark ticket as ready.');
    } finally {
      setUpdatingTicket(null);
    }
  };

  const handleTicketClickToDelivered = async (ticket) => {
    setUpdatingTicket(ticket.id_ticket_cle);
    const id_account_caisse = localStorage.getItem('id_account_caisse');

    try {
      const response = await axios.post(
        'https://apiTicket.lalivraison.ma/api/update-ticket-livree',
        {
          id_ticket_cle: ticket.id_ticket_cle,
          restaurantId: ticket.id_caisse,
          id_account_caisse,
        },
        { timeout: 5000 }
      );

      if (response.status === 200) {
        setError('');
        await fetchQueueData();
      }
    } catch (error) {
      console.error('Error updating ticket to delivered:', error);
      setError(error.response?.data?.message || 'Failed to mark ticket as delivered.');
    } finally {
      setUpdatingTicket(null);
    }
  };

  const chunkArray = (array, chunkSize) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      queueDisplayRef.current?.requestFullscreen?.().catch((err) => {
        console.error(`Error enabling fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen?.();
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const calculateTicketsPerColumn = () => {
    if (!isFullscreen) return 8; // Normal view: 8 tickets per column

    // In fullscreen mode, always return 11 tickets per column
    return 11;
  };

  const ticketsPerColumn = calculateTicketsPerColumn();
  const pendingColumns = chunkArray(pendingOrders, ticketsPerColumn);
  const inPreparationColumns = chunkArray(inPreparation, ticketsPerColumn);
  const readyToServeColumns = chunkArray(readyToServe, ticketsPerColumn);

  const handleMenuChange = (menu) => {
    setSelectedMenu(menu);
  };

  return (
    <div className="queue-display" ref={queueDisplayRef}>
      <header className="header">
        <div>{formatDateTime(currentTime)}</div>
        <div className="logo">
          <img
            src="/lacaisse_logo.png"
            alt="La Caisse Logo"
            style={{ width: '170px', height: 'auto' }}
          />
        </div>
        <div className="header-buttons">
          <MenuSelect onMenuChange={handleMenuChange} />
          <button onClick={toggleFullscreen} className="fullscreen-btn">
            {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
          </button>
          <button onClick={handleLogout} className="logout-btn">
            Déconnexion
          </button>
        </div>
      </header>
      <div className="queue-container">
        {showPendingSection && (
          <div className="section">
            <div className="section-header">
              <h2>À Valider en caisse</h2>
              {(loading || backgroundLoading) && <ClipLoader size={20} color="#ff4d4d" />}
            </div>
            <div className="tickets">
              {pendingColumns.length > 0 ? (
                pendingColumns.map((column, colIndex) => (
                  <div key={colIndex} className="ticket-column">
                    {column.map((ticket) => (
                      <div key={ticket.id_ticket} className="ticket">
                        {updatingTicket === ticket.id_ticket ? (
                          <ClipLoader size={15} color="#ffffff" />
                        ) : (
                          ticket.compteur
                        )}
                      </div>
                    ))}
                  </div>
                ))
              ) : (
                <div className="no-tickets">Aucun ticket à valider</div>
              )}
            </div>
          </div>
        )}
        <div className="section">
          <div className="section-header">
            <h2>En préparation</h2>
            {(loading || backgroundLoading) && <ClipLoader size={20} color="#ff4d4d" />}
          </div>
          <div className="tickets">
            {inPreparationColumns.length > 0 ? (
              inPreparationColumns.map((column, colIndex) => (
                <div key={colIndex} className="ticket-column">
                  {column.map((ticket) => (
                    <div
                      key={ticket.id_ticket}
                      className="ticket"
                      onClick={() => handleTicketClickToReady(ticket)}
                      style={{ cursor: 'pointer' }}
                    >
                      {updatingTicket === ticket.id_ticket_cle ? (
                        <ClipLoader size={15} color="#ffffff" />
                      ) : ticket.display_ticket ? (
                        extractNumbers(ticket.display_ticket) || ticket.compteur
                      ) : (
                        ticket.compteur
                      )}
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <div className="no-tickets">Aucun ticket en préparation</div>
            )}
          </div>
        </div>
        <div className="section">
          <div className="section-header">
            <h2>Prêt à servir</h2>
            {(loading || backgroundLoading) && <ClipLoader size={20} color="#ff4d4d" />}
          </div>
          <div className="tickets">
            {readyToServeColumns.length > 0 ? (
              readyToServeColumns.map((column, colIndex) => (
                <div key={colIndex} className="ticket-column">
                  {column.map((ticket) => (
                    <div
                      key={ticket.id_ticket}
                      className="ticket"
                      onClick={() => handleTicketClickToDelivered(ticket)}
                      style={{ cursor: 'pointer' }}
                    >
                      {updatingTicket === ticket.id_ticket_cle ? (
                        <ClipLoader size={15} color="#ffffff" />
                      ) : ticket.display_ticket ? (
                        extractNumbers(ticket.display_ticket) || ticket.compteur
                      ) : (
                        ticket.compteur
                      )}
                    </div>
                  ))}
                </div>
              ))
            ) : (
              <div className="no-tickets">Aucun ticket prêt à servir</div>
            )}
          </div>
        </div>
      </div>
      {error && <div className="error-message">{error}</div>}
      <footer className="footer">
        Suivez vos commandes ici. Bon appétit et à très bientôt!
      </footer>
      {lastUpdated && (
        <div className="last-updated">
          Dernière mise à jour: {formatDateTime(lastUpdated)}
        </div>
      )}
    </div>
  );
};

export default QueueDisplay;